// =============================================================================
// useLektor — JEDEN lektor Nexusa dla Kina Genesis i obu Akademii.
//
// 13.09.2026 (Jan-Dar): Kino Genesis miało własną syntezę bez pamięci
// podręcznej (każde „Poprzedni" płaciło ElevenLabs drugi raz), Akademia
// miała pamięć w ref, ale traciła ją przy odświeżeniu, a prezentacja modułów
// nie mówiła wcale. Ten hook jest jedną implementacją: synteza przez serwer
// (`/api/nexus/voice/synthesize`, głos Nexusa z `speech_config` albo `voice`
// eksperta), pamięć per klucz w ref + sessionStorage z sufitem, zapas
// w `speechSynthesis` pl-PL, gdy serwer nie odpowie.
//
// `mow()` rozwiązuje się DOPIERO po wybrzmieniu — na tym opiera się rytm
// prezentacji („następna scena po końcu mowy", nie po 9 s). Zwraca `true`,
// gdy wypowiedź wybrzmiała do końca, `false`, gdy została przerwana
// (`zatrzymaj()`, nowa wypowiedź) albo lektor jest wyłączony — wołający
// nie przewija sceny po pauzie.
//
// Synteza idzie na rachunek ORGANIZACJI (decyzja właściciela z 29.10.2026):
// serwer wymaga `tenant_id` i członkostwa w nim, a koszt liczy w jej
// `nexus_usage`. Lektor dostaje więc `tenantId` od wołającego; bez organizacji
// nie pyta serwera wcale (400 i tak by przyszło) i od razu mówi zapasem
// przeglądarki — nie zgaduje organizacji.
//
// Domyślny `pobierzMowe` ładuje `nexusApi` DYNAMICZNIE: statyczny import
// ciągnie `lib/supabase`, a ten rzuca bez zmiennych środowiskowych — czyli
// w każdym teście RTL. Komponent z tym hookiem zostaje czysty do renderu.
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';

import { kluczMowy, mimeMowy, odczytajPamiecLektora, zapiszPamiecLektora } from './lektorCore';

export interface Mowa {
  audio_base64: string;
  format?: string;
}

/** Synteza tekstu → nagranie. Domyślnie serwer Nexusa; w teście — atrapa. */
export type PobierzMowe = (tekst: string, voice?: string) => Promise<Mowa>;

export interface OpcjeLektora {
  pobierzMowe?: PobierzMowe;
  /** `false` = `mow()` milczy i od razu oddaje `false` (bramka: „cisza"). */
  wlaczony?: boolean;
  /** Organizacja, na której rachunek idzie synteza (`tenant_id`). */
  tenantId?: string | null;
}

export interface Lektor {
  /** Czyta tekst; rozwiązuje się po wybrzmieniu (`true`) albo przerwaniu (`false`). */
  mow(klucz: string, tekst: string, voice?: string): Promise<boolean>;
  zatrzymaj(): void;
  mowi: boolean;
}

const SYNTEZA_TIMEOUT_MS = 60_000;

/** Synteza przez serwer Nexusa na rachunek `tenantId`; bez organizacji — odmowa lokalna. */
export function pobierzMoweDla(tenantId: string | null | undefined): PobierzMowe {
  return async (tekst, voice) => {
    if (!tenantId) throw new Error('Synteza mowy wymaga organizacji.');
    const { nexusPost } = await import('../../lib/nexusApi');
    return nexusPost<Mowa>(
      '/api/nexus/voice/synthesize',
      voice ? { tenant_id: tenantId, text: tekst, voice } : { tenant_id: tenantId, text: tekst },
      { timeoutMs: SYNTEZA_TIMEOUT_MS }
    );
  };
}

export function useLektor(opcje: OpcjeLektora = {}): Lektor {
  const { wlaczony = true, tenantId } = opcje;
  const pobierzMowe = opcje.pobierzMowe ?? pobierzMoweDla(tenantId);
  const [mowi, setMowi] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const utterRef = useRef<SpeechSynthesisUtterance | null>(null);
  // Bieżąca wypowiedź — jej `zakoncz(false)` woła `zatrzymaj()` i każda
  // następna `mow()`, żeby poprzednia obietnica nie wisiała w nieskończoność.
  const zakonczRef = useRef<((wybrzmialo: boolean) => void) | null>(null);
  const pamiecRef = useRef<Map<string, string> | null>(null);
  const pobierzRef = useRef(pobierzMowe);
  pobierzRef.current = pobierzMowe;

  const pamiec = () => {
    if (!pamiecRef.current) pamiecRef.current = odczytajPamiecLektora();
    return pamiecRef.current;
  };

  const zatrzymaj = useCallback(() => {
    const audio = audioRef.current;
    if (audio) {
      audio.onended = null;
      audio.onerror = null;
      audio.pause();
      audioRef.current = null;
    }
    if (utterRef.current) {
      utterRef.current.onend = null;
      utterRef.current.onerror = null;
      utterRef.current = null;
      window.speechSynthesis?.cancel();
    }
    const zakoncz = zakonczRef.current;
    zakonczRef.current = null;
    setMowi(false);
    zakoncz?.(false);
  }, []);

  const odtworz = useCallback((mowa: Mowa): Promise<boolean> => {
    return new Promise((resolve) => {
      const audio = new Audio(`data:${mimeMowy(mowa.format)};base64,${mowa.audio_base64}`);
      audioRef.current = audio;
      zakonczRef.current = resolve;
      setMowi(true);
      const koniec = (wybrzmialo: boolean) => () => {
        if (audioRef.current !== audio) return;
        audioRef.current = null;
        zakonczRef.current = null;
        setMowi(false);
        resolve(wybrzmialo);
      };
      audio.onended = koniec(true);
      audio.onerror = koniec(false);
      // `play()` oddaje obietnicę w przeglądarkach; w jsdom i starych WebView
      // oddaje `undefined` — bez tej osłony lektor wisiałby na TypeError.
      const odtwarzanie = audio.play() as Promise<void> | undefined;
      odtwarzanie?.catch(koniec(false));
    });
  }, []);

  const zapas = useCallback((tekst: string): Promise<boolean> => {
    return new Promise((resolve) => {
      const synth = window.speechSynthesis;
      if (!synth || typeof SpeechSynthesisUtterance === 'undefined') {
        resolve(false);
        return;
      }
      const utter = new SpeechSynthesisUtterance(tekst);
      utter.lang = 'pl-PL';
      utterRef.current = utter;
      zakonczRef.current = resolve;
      setMowi(true);
      const koniec = (wybrzmialo: boolean) => () => {
        if (utterRef.current !== utter) return;
        utterRef.current = null;
        zakonczRef.current = null;
        setMowi(false);
        resolve(wybrzmialo);
      };
      utter.onend = koniec(true);
      utter.onerror = koniec(false);
      synth.cancel();
      synth.speak(utter);
    });
  }, []);

  const mow = useCallback(
    async (klucz: string, tekst: string, voice?: string): Promise<boolean> => {
      zatrzymaj();
      const czysty = tekst.trim();
      if (!wlaczony || !czysty) return false;

      const k = kluczMowy(klucz, czysty, voice);
      let mowa: Mowa | null = null;
      const zapamietane = pamiec().get(k);
      if (zapamietane) {
        mowa = { audio_base64: zapamietane, format: 'mp3' };
      } else {
        try {
          const pobrana = await pobierzRef.current(czysty, voice);
          if (pobrana?.audio_base64) {
            mowa = pobrana;
            const m = pamiec();
            m.set(k, pobrana.audio_base64);
            pamiecRef.current = zapiszPamiecLektora(m);
          }
        } catch {
          // niżej — zapas przeglądarkowy; obcy głos jest mniejszym złem niż cisza
        }
      }
      if (mowa) return odtworz(mowa);
      return zapas(czysty);
    },
    [wlaczony, zatrzymaj, odtworz, zapas]
  );

  // Głos nie może przeżyć komponentu.
  useEffect(() => () => zatrzymaj(), [zatrzymaj]);

  return { mow, zatrzymaj, mowi };
}
